import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkspace } from "@/shared/hooks/api/useWorkspace";
import { useIsSuperAdminRole } from "@/shared/hooks/use-system-role";
import { useToast } from "@/shared/hooks/use-toast";
import { useAgentBase } from "@/features/Agents/hooks/useAgentBase";
import { AgentBase } from "@/features/Agents/types/AgentBaseInterface";
import { AgentBaseService } from "@/features/Agents/services/agentBaseService";
import { Button } from "@/shared/components/ui/button";
import { Card } from "@/shared/components/ui/card";
import { Trash, Search, Filter, Plus, X, Loader2, Settings } from "lucide-react";
import { CategoryFilter, FilterState, ColumnState } from "@/shared/components/ui/category-filter";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { Alert, AlertDescription } from "@/shared/components/ui/alert";
import { Badge } from "@/shared/components/ui/badge";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/shared/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { DatePicker } from "@/shared/components/ui/date-picker";
import { format } from "date-fns";
import { PipelineSelectorModal } from "./PipelineSelectorModal";

export function KnowledgePipelines() {
  const { t } = useTranslation();
  const { currentWorkspace } = useWorkspace();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isSuperAdmin } = useIsSuperAdminRole();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchDate, setSearchDate] = useState<Date | undefined>(undefined);
  const [searchFieldType, setSearchFieldType] = useState<'text' | 'date'>('text');
  const [filters, setFilters] = useState<FilterState>({});
  const [visibleColumns, setVisibleColumns] = useState<ColumnState>({
    name: true, // Set to true as this is a default search field in the backend
    created_at: false,
    updated_at: false,
    status: false,
    actions: false
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [deletingAgentId, setDeletingAgentId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPipelineSelectorOpen, setIsPipelineSelectorOpen] = useState(false);

  const setFieldType = (type: 'text' | 'date') => {
    setSearchFieldType(type);
    if (type === 'text') {
      setSearchDate(undefined);
    } else {
      setSearchTerm('');
    }
  };

  const effectiveSearchTerm = searchFieldType === 'text'
    ? searchTerm
    : (searchDate ? format(searchDate, 'yyyy-MM-dd') : '');

  const {
    data: agentData,
    isLoading: isLoadingAgents,
    isError: isAgentsError,
    error: agentsError
  } = useAgentBase(
    {
      workspaceId: currentWorkspace?.id || "",
      page: currentPage,
      pageSize,
      search: effectiveSearchTerm || undefined,
      type: 1, // Filtrando apenas agentes do tipo Pipeline
      filters: Object.entries(filters).reduce((acc, [key, values]) => {
        if (values && values.length > 0) {
          acc[key] = values;
        }
        return acc;
      }, {} as Record<string, string[]>),
      searchFieldType
    }
  );

  const queryClient = useQueryClient();

  if (!currentWorkspace?.id) {
    return null;
  }

  // Renderiza o conteúdo da tabela
  const renderTableContent = () => {
    if (isLoadingAgents) {
      return Array(3).fill(0).map((_, index) => (
        <TableRow key={index}>
          <TableCell><Skeleton className="h-4 w-[100px]" /></TableCell>
          <TableCell><Skeleton className="h-4 w-[150px]" /></TableCell>
          <TableCell><Skeleton className="h-4 w-[100px]" /></TableCell>
          <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
          <TableCell><Skeleton className="h-4 w-[80px]" /></TableCell>
        </TableRow>
      ));
    }

    if (isAgentsError) {
      console.error("Agents Error:", agentsError);
      return (
        <TableRow>
          <TableCell colSpan={6}>
            <Alert variant="destructive">
              <AlertDescription>
                {agentsError instanceof Error
                  ? agentsError.message
                  : t('knowledge.pipelines.error_loading')}
              </AlertDescription>
            </Alert>
          </TableCell>
        </TableRow>
      );
    }

    if (!agentData?.data?.length) {
      return (
        <TableRow>
          <TableCell colSpan={6} className="text-center">
            {t('knowledge.pipelines.no_data')}
          </TableCell>
        </TableRow>
      );
    }

    return agentData.data.map((agent: AgentBase) => (
      <TableRow key={agent.id}>
        <TableCell>
          <span>{agent.name}</span>
        </TableCell>
        <TableCell>{agent.created_at ? new Date(agent.created_at).toLocaleString() : '-'}</TableCell>
        <TableCell>{agent.updated_at ? new Date(agent.updated_at).toLocaleString() : '-'}</TableCell>
        <TableCell>
          <Badge variant={
            agent.status === 'active'
              ? 'success'
              : agent.status === 'error'
                ? 'destructive'
                : 'warning'
          }>
            {agent.status}
          </Badge>
        </TableCell>
        <TableCell>
          <div className="flex items-center justify-center space-x-2">
            {isSuperAdmin && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => navigate(`/agents/${agent.id}/settings`)}
                title={t('common.actions.edit')}
              >
                <Settings className="h-4 w-4" />
              </Button>
            )}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => handleDeleteClick(agent.id)}
              className="text-red-600 hover:text-red-700"
              title={t('common.actions.delete')}
            >
              <Trash className="h-4 w-4" />
            </Button>
          </div>
        </TableCell>
      </TableRow>
    ));
  };

  const handleDeleteClick = (id: string) => {
    setDeletingAgentId(id);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingAgentId) return;

    try {
      setIsDeleting(true);

      // Call the actual uninstall API
      const result = await AgentBaseService.uninstallAgent(deletingAgentId);

      if (result.error) {
        throw new Error(result.error.message || 'Erro ao excluir pipeline');
      }

      // Invalidate and refetch
      queryClient.invalidateQueries({
        queryKey: ['agent-base']
      });

      toast({
        title: t('common.messages.deleteSuccess'),
        description: t('common.messages.deleteSuccessDescription'),
      });

      // Update local cache to remove the deleted item
      queryClient.setQueryData(
        ['agent-base', { workspaceId: currentWorkspace?.id, type: 1 }],
             // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (oldData: any) => oldData && {
          ...oldData,
          total: oldData.total - 1,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
          data: oldData.data.filter((item: any) => item.id !== deletingAgentId)
        }
      );
    } catch (error) {
      console.error('Error deleting agent:', error);
      toast({
        variant: "destructive",
        title: t('common.error'),
        description: t('common.messages.deleteError'),
      });
    } finally {
      setIsDeleting(false);
      setIsDeleteDialogOpen(false);
      setDeletingAgentId(null);
    }
  };

  const handlePipelineInstalled = async () => {
    // Refresh the pipeline list
    await queryClient.invalidateQueries({
      queryKey: ['agent-base']
    });
  };

  return (
    <div className="space-y-4">
      <Card>
        <div className="p-4 space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-2">
              {searchFieldType === 'text' ? (
                <div className="relative">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground z-10 pointer-events-none" />
                  <Input
                    placeholder={t('knowledge.pipelines.actions.search')}
                    className="pl-8 w-[250px]"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                  {searchTerm && (
                    <Button
                      variant="ghost"
                      className="absolute right-0 top-0 h-9 w-9 p-0"
                      onClick={() => setSearchTerm("")}
                    >
                      <X className="h-4 w-4" />
                      <span className="sr-only">{t('knowledge.pipelines.actions.clear')}</span>
                    </Button>
                  )}
                </div>
              ) : (
                <div className="relative w-[250px]">
                  <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground z-10" />
                  <DatePicker
                    value={searchDate}
                    onChange={(date: Date | undefined) => setSearchDate(date)}
                    placeholder={t('knowledge.pipelines.actions.search')}
                    width="250px"
                    className="pl-8 w-[250px]"
                  />
                </div>
              )}
              <CategoryFilter
                trigger={
                  <Button variant="outline" size="icon">
                    <Filter className="h-4 w-4" />
                    <span className="sr-only">{t('knowledge.pipelines.actions.filter')}</span>
                  </Button>
                }
                selectedFilters={filters}
                onChange={setFilters}
                onClear={() => {
                  setFilters({});

                  // Redefinir para o campo de texto
                  setFieldType('text');

                  // Reset to default columns (name) when clearing
                  setVisibleColumns({
                    name: true, // Default search field in the backend
                    created_at: false,
                    updated_at: false,
                    status: false,
                    actions: false
                  });
                }}
                columns={[
                  { id: 'name', name: t('knowledge.pipelines.fields.name'), type: 'text' },
                  { id: 'created_at', name: t('knowledge.pipelines.fields.created_at'), type: 'date' },
                  { id: 'updated_at', name: t('knowledge.pipelines.fields.updated_at'), type: 'date' },
                  { id: 'status', name: t('knowledge.pipelines.fields.status'), type: 'text' },
                ]}
                selectedColumns={visibleColumns}
                onColumnChange={(newColumns) => {

                  const wasDateColumnActive = visibleColumns['created_at'] || visibleColumns['updated_at'];
                  const isDateColumnActive = newColumns['created_at'] || newColumns['updated_at'];

                  if (isDateColumnActive && !wasDateColumnActive) {
                    setFieldType('date');
                  } else if (!isDateColumnActive && wasDateColumnActive) {
                    setFieldType('text');
                  }
                  setVisibleColumns(newColumns);
                }}
              />
            </div>
            <div className="flex items-center space-x-2">
              <Button onClick={() => setIsPipelineSelectorOpen(true)}>
                <Plus className="mr-2 h-4 w-4" />
                {t('knowledge.pipelines.actions.create')}
              </Button>
            </div>
          </div>

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('knowledge.pipelines.fields.name')}</TableHead>
                  <TableHead>{t('knowledge.pipelines.fields.created_at')}</TableHead>
                  <TableHead>{t('knowledge.pipelines.fields.updated_at')}</TableHead>
                  <TableHead>{t('knowledge.pipelines.fields.status')}</TableHead>
                  <TableHead className="text-center">{t('knowledge.pipelines.fields.actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {renderTableContent()}
              </TableBody>
            </Table>
          </div>

          {agentData && agentData.total > 0 && (
            <div className="flex items-center justify-between px-4 mt-4">
              <div className="flex-1 text-sm text-muted-foreground">
                {isLoadingAgents ? (
                  <Skeleton className="h-4 w-[250px]" />
                ) : (
                  t('knowledge.pipelines.showing_pipelines', {
                    start: (currentPage - 1) * pageSize + 1,
                    end: Math.min(currentPage * pageSize, agentData?.total || 0),
                    total: agentData?.total || 0
                  })
                )}
              </div>

              <div className="flex-1 flex justify-center items-center">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      {currentPage === 1 ? (
                        <PaginationPrevious className="cursor-not-allowed opacity-50" />
                      ) : (
                        <PaginationPrevious onClick={() => setCurrentPage(Math.max(1, currentPage - 1))} />
                      )}
                    </PaginationItem>

                    {/* Primeira página */}
                    {currentPage > 2 && (
                      <PaginationItem>
                        <PaginationLink onClick={() => setCurrentPage(1)}>
                          1
                        </PaginationLink>
                      </PaginationItem>
                    )}

                    {/* Ellipsis no início */}
                    {currentPage > 3 && (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    )}

                    {/* Página anterior (se não for primeira) */}
                    {currentPage > 1 && (
                      <PaginationItem>
                        <PaginationLink onClick={() => setCurrentPage(currentPage - 1)}>
                          {currentPage - 1}
                        </PaginationLink>
                      </PaginationItem>
                    )}

                    {/* Página atual */}
                    <PaginationItem>
                      <PaginationLink isActive>
                        {currentPage}
                      </PaginationLink>
                    </PaginationItem>

                    {/* Próxima página */}
                    {currentPage < Math.ceil((agentData?.total || 0) / pageSize) && (
                      <PaginationItem>
                        <PaginationLink onClick={() => setCurrentPage(currentPage + 1)}>
                          {currentPage + 1}
                        </PaginationLink>
                      </PaginationItem>
                    )}

                    {/* Ellipsis no final */}
                    {currentPage < Math.ceil((agentData?.total || 0) / pageSize) - 2 && (
                      <PaginationItem>
                        <PaginationEllipsis />
                      </PaginationItem>
                    )}

                    {/* Última página */}
                    {currentPage < Math.ceil((agentData?.total || 0) / pageSize) - 1 && (
                      <PaginationItem>
                        <PaginationLink onClick={() => setCurrentPage(Math.ceil((agentData?.total || 0) / pageSize))}>
                          {Math.ceil((agentData?.total || 0) / pageSize)}
                        </PaginationLink>
                      </PaginationItem>
                    )}

                    <PaginationItem>
                      {currentPage === Math.ceil((agentData?.total || 0) / pageSize) ? (
                        <PaginationNext className="cursor-not-allowed opacity-50" />
                      ) : (
                        <PaginationNext onClick={() => setCurrentPage(Math.min(Math.ceil((agentData?.total || 0) / pageSize), currentPage + 1))} />
                      )}
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>

              <div className="flex items-center gap-2 justify-end">
                <span className="text-sm text-muted-foreground">{t('knowledge.pipelines.itemsPerPage')}</span>
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => {
                    setPageSize(parseInt(value));
                    setCurrentPage(1);
                  }}
                >
                  <SelectTrigger className="w-[70px]">
                    <SelectValue placeholder={pageSize.toString()} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="5">5</SelectItem>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('common.messages.deleteConfirmationTitle')}</DialogTitle>
            <DialogDescription>
              {t('common.messages.deleteConfirmationDescription')}
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={() => setIsDeleteDialogOpen(false)}
              disabled={isDeleting}
            >
              {t('common.actions.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.actions.delete')}
                </>
              ) : (
                t('common.actions.delete')
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Pipeline Selector Modal */}
      <PipelineSelectorModal
        isOpen={isPipelineSelectorOpen}
        onClose={() => setIsPipelineSelectorOpen(false)}
        onPipelineInstalled={handlePipelineInstalled}
      />
    </div>
  );
}
